import KeenSlider from "keen-slider";
import { debounce } from "@allwyn/utils/debounce";
import { whenDomReady } from "@allwyn/utils/document-ready";

const ELEMENTS = {
    root: "company-history-root",
    carousel: ".company-history-timeline__carousel",
    item: ".company-history-timeline-item",
    itemToggleIcons: ".company-history-timeline-item__text-toggle",
    itemText: ".company-history-timeline-item__text",
    contentWrap: ".company-history-timeline-item__content-wrap",
    timelineContainer: ".company-history-timeline",
};

const initCompanyHistory = () => {
    const root = document.getElementById(ELEMENTS.root);
    if (!root) return;

    const sliderElem = root.querySelector<HTMLElement>(ELEMENTS.carousel);
    if (!sliderElem) return;

    const slider = new KeenSlider(sliderElem, {
        slides: {
            perView: "auto",
        },
        selector: ELEMENTS.item,
    });

    const contentWrappedClass = "company-history-timeline-item__content--wrapped";
    const contentWrappableClass = "company-history-timeline-item__content--wrappable";

    const checkTextHeight = (textElem: HTMLElement, parentContentElem: HTMLElement) => {
        // Remove existing classes to get natural height
        parentContentElem.classList.remove(contentWrappedClass, contentWrappableClass);

        if (textElem.offsetHeight > 120) {
            parentContentElem.classList.add(contentWrappedClass, contentWrappableClass);
        }
    };

    const getItemElements = (icon: Element) => {
        const parent = icon.parentElement;
        if (!parent) return null;

        const textElem = parent.querySelector<HTMLElement>(ELEMENTS.itemText);
        if (!textElem) return null;

        return { textElem, parentContentElem: parent };
    };

    const itemToggleIcons = sliderElem.querySelectorAll(ELEMENTS.itemToggleIcons);
    const clickHandlers = new Map<Element, EventListener>();

    itemToggleIcons.forEach((icon) => {
        const elements = getItemElements(icon);
        if (!elements) return;

        const { textElem, parentContentElem } = elements;
        const contentWrap = textElem.closest(ELEMENTS.contentWrap);
        if (contentWrap) {
            contentWrap.classList.add("company-history-timeline-item__content-wrap--inited");
        }

        checkTextHeight(textElem, parentContentElem);

        const clickHandler = () => {
            const timelineContainer = document.querySelector(ELEMENTS.timelineContainer);

            if (parentContentElem.classList.contains(contentWrappedClass)) {
                parentContentElem.classList.remove(contentWrappedClass);
                const expandedItems = sliderElem.querySelectorAll(
                    ".company-history-timeline-item__content--wrappable:not(.company-history-timeline-item__content--wrapped)"
                );
                if (expandedItems.length === 0) {
                    timelineContainer?.classList.remove("has-expanded-items");
                }
            } else {
                parentContentElem.classList.add(contentWrappedClass);
                timelineContainer?.classList.add("has-expanded-items");
            }
        };

        icon.addEventListener("click", clickHandler);
        clickHandlers.set(icon, clickHandler);
    });

    const handleResize = debounce(() => {
        itemToggleIcons.forEach((icon) => {
            const elements = getItemElements(icon);

            if (elements) checkTextHeight(elements.textElem, elements.parentContentElem);
        });
    }, 50);

    window.addEventListener("resize", handleResize);

    const cleanup = () => {
        window.removeEventListener("resize", handleResize);
        clickHandlers.forEach((handler, icon) => icon.removeEventListener("click", handler));
        slider.destroy();
    };

    window.addEventListener("beforeunload", cleanup);
};

await whenDomReady();
initCompanyHistory();
